import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCheck, FiClock, FiLayers, FiShoppingBag, FiUser } from 'react-icons/fi';
import { usePageSEO } from '../hooks/useSEO';
import { getImageUrl } from '../utils/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { useCart } from '../contexts/CartContext';
import { setExpertRefCookie, getPersonaLabel } from '../utils/expertRef';
import { API_BASE_URL } from '../config';
import { expertTheme, focusRing, reducedMotion } from '../theme/expertTheme';

const pulse = keyframes`50% { opacity: .55; }`;

const Page = styled.div`
  min-height: 100vh;
  padding: 28px 0 112px;
  background: radial-gradient(circle at 12% 4%, rgba(240,100,50,.09), transparent 30rem), ${expertTheme.colors.canvas};
  color: ${expertTheme.colors.ink};
`;

const Container = styled.div`
  width: min(${expertTheme.container}, calc(100% - 48px));
  margin: 0 auto;
  @media (max-width: 640px) { width: min(100% - 32px, ${expertTheme.container}); }
`;

const Breadcrumb = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 22px;
  color: #81847c;
  font-size: 13px;
  a { color: ${expertTheme.colors.sage}; font-weight: 700; }
`;

const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(360px, .92fr);
  min-height: 600px;
  overflow: hidden;
  border-radius: 34px;
  background: ${expertTheme.colors.charcoal};
  box-shadow: ${expertTheme.shadow.soft};
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Cover = styled.div`
  min-height: 500px;
  background: #dcd7cd center / cover no-repeat;
  @media (max-width: 620px) { min-height: 330px; }
`;

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(34px, 5vw, 70px);
  color: #fff;
`;

const Eyebrow = styled.span`
  color: #eaa98d;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 18px 0 22px;
  font-family: var(--font-body);
  font-size: clamp(2.4rem, 5vw, 4.5rem);
  font-weight: 760;
  line-height: .98;
  letter-spacing: -.05em;
`;

const Facts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 28px;
`;

const Fact = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 38px;
  padding: 8px 12px;
  border: 1px solid rgba(255,255,255,.18);
  border-radius: ${expertTheme.radius.pill};
  color: rgba(255,255,255,.76);
  font-size: 13px;
`;

const Price = styled.strong`
  display: block;
  margin-top: auto;
  font-size: clamp(2rem, 3vw, 3rem);
  font-variant-numeric: tabular-nums;
`;

const BuyButton = styled.button`
  min-height: 54px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 22px;
  padding: 14px 24px;
  border-radius: ${expertTheme.radius.pill};
  background: ${p => p.$added ? expertTheme.colors.sage : expertTheme.colors.accent};
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  transition: background 180ms ease, transform 180ms ease;
  ${focusRing}
  ${reducedMotion}
  &:hover { background: ${p => p.$added ? expertTheme.colors.sage : expertTheme.colors.accentDark}; transform: translateY(-2px); }
  &:disabled { cursor: default; transform: none; }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: clamp(28px, 5vw, 68px);
  align-items: start;
  padding-top: clamp(64px, 8vw, 104px);
  @media (max-width: 850px) { grid-template-columns: 1fr; }
`;

const Description = styled.article`
  padding: clamp(28px, 5vw, 56px);
  border-radius: ${expertTheme.radius.large};
  background: ${expertTheme.colors.surface};
  box-shadow: 0 12px 42px rgba(42,38,30,.06);
  h2 { margin: 0 0 28px; font-family: var(--font-body); font-size: clamp(2rem, 4vw, 3.6rem); letter-spacing: -.04em; }
`;

const Prose = styled.div`
  color: ${expertTheme.colors.inkSoft};
  font-size: 17px;
  line-height: 1.78;
  p { margin: 0 0 20px; }
  h2, h3 { margin: 34px 0 14px; color: ${expertTheme.colors.ink}; font-family: var(--font-body); letter-spacing: -.02em; }
  ul, ol { margin: 20px 0; padding-left: 22px; }
  ul { list-style: disc; }
  ol { list-style: decimal; }
  li { margin-bottom: 10px; }
`;

const ExpertCard = styled.aside`
  position: sticky;
  top: 24px;
  padding: 24px;
  border-radius: ${expertTheme.radius.medium};
  background: ${expertTheme.colors.sageSoft};
  span { color: ${expertTheme.colors.sage}; font-size: 11px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
  img { width: 100%; aspect-ratio: 1; margin: 18px 0; object-fit: cover; object-position: top; border-radius: 18px; }
  h2 { margin: 0 0 12px; font-family: var(--font-body); font-size: 24px; letter-spacing: -.025em; }
  a { display: inline-flex; align-items: center; gap: 8px; color: ${expertTheme.colors.ink}; font-weight: 800; ${focusRing} }
  @media (max-width: 850px) { position: static; }
`;

const State = styled.div`
  width: min(760px, calc(100% - 32px));
  min-height: 420px;
  margin: 60px auto;
  display: grid;
  place-items: center;
  padding: 40px;
  border-radius: ${expertTheme.radius.large};
  background: #fff;
  text-align: center;
  animation: ${p => p.$loading ? pulse : 'none'} 1.2s ease-in-out infinite;
  h1 { font-family: var(--font-body); }
  p { color: ${expertTheme.colors.inkSoft}; }
  a { color: ${expertTheme.colors.accent}; font-weight: 800; }
  ${reducedMotion}
`;

function formatPrice(value) {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? `${number.toLocaleString('ru-RU')} ₽` : 'Цена по запросу';
}

const DigitalProductPage = () => {
  const { slug, programSlug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [added, setAdded] = useState(false);
  const { addDigitalToCart } = useCart();

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    fetch(`${API_BASE_URL}/experts/${slug}/programs/${programSlug}`, { credentials: 'include', signal: controller.signal })
      .then(response => response.json())
      .then(result => {
        if (!result.success || !result.data?.product) throw new Error('not-found');
        setData(result.data);
        setStatus('success');
        const referralCode = result.data?.expert?.referralCode || result.data?.expert?.referral_code;
        if (referralCode) setExpertRefCookie(referralCode);
      })
      .catch(error => { if (error.name !== 'AbortError') setStatus('error'); });
    return () => controller.abort();
  }, [slug, programSlug]);

  const product = data?.product;
  const expert = data?.expert;

  usePageSEO({
    title: product ? `${product.title} — ${expert?.displayName || ''}` : 'Программа',
    description: product?.description?.replace(/<[^>]+>/g, ' ').slice(0, 160) || '',
    jsonLd: product ? {
      '@context': 'https://schema.org', '@type': 'Product', name: product.title,
      description: product.description, image: product.cover_image ? getImageUrl(product.cover_image) : undefined,
      offers: { '@type': 'Offer', price: product.price, priceCurrency: 'RUB' }
    } : null
  });

  if (status === 'loading') return <Page><State $loading><p>Готовим программу…</p></State></Page>;
  if (status === 'error' || !product) return <Page><State><div><h1>Программа не найдена</h1><p>Она могла быть снята с публикации или ссылка устарела.</p><Link to={`/expert/${slug}`}>Вернуться к эксперту</Link></div></State></Page>;

  const displayName = expert?.displayName || expert?.display_name;
  const photoUrl = expert?.photoUrl || expert?.photo_url;
  const personaType = expert?.personaType || expert?.persona_type;

  return (
    <Page>
      <Container>
        <Breadcrumb><Link to="/experts"><FiArrowLeft /> Эксперты</Link><span>/</span><Link to={`/expert/${slug}`}>{displayName}</Link><span>/</span><span>{product.title}</span></Breadcrumb>
        <Hero>
          <Cover role="img" aria-label={`Обложка программы «${product.title}»`} style={{ backgroundImage: `url(${getImageUrl(product.cover_image)})` }} />
          <HeroCopy>
            <Eyebrow>Авторская digital-программа</Eyebrow>
            <Title>{product.title}</Title>
            <Facts>
              {product.duration_label ? <Fact><FiClock />{product.duration_label}</Fact> : null}
              {product.difficulty ? <Fact><FiLayers />{product.difficulty}</Fact> : null}
              {displayName ? <Fact><FiUser />{displayName}</Fact> : null}
            </Facts>
            <Price>{formatPrice(product.price)}</Price>
            <BuyButton type="button" $added={added} disabled={added} onClick={() => { addDigitalToCart(product); setAdded(true); window.setTimeout(() => setAdded(false), 2200); }}>{added ? <><FiCheck /> Добавлено в корзину</> : <><FiShoppingBag /> Добавить в корзину</>}</BuyButton>
          </HeroCopy>
        </Hero>

        <ContentGrid>
          <Description><h2>О программе</h2><Prose dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description || '<p>Описание программы скоро появится.</p>') }} /></Description>
          {expert ? <ExpertCard><span>{getPersonaLabel(personaType)}</span>{photoUrl ? <img src={getImageUrl(photoUrl)} alt={displayName} /> : null}<h2>{displayName}</h2><Link to={`/expert/${expert.slug || slug}`}>Профиль эксперта <FiArrowLeft style={{ transform: 'rotate(180deg)' }} /></Link></ExpertCard> : null}
        </ContentGrid>
      </Container>
    </Page>
  );
};

export default DigitalProductPage;
